import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getMembersByStatus } from "../../services/memberServices";
import { getAllDesignations } from "../../services/leaderDesingation";
import MembersTable from "./MemberTable";
import MemberRoleModal from "./MemberRoleModal";
import CustomSelect from "../../components/admin/common/CustomSelect";
import { getCookie } from "../../utils";
import { useUserModulePermissions } from "../../hooks/useUserModulePermissions";

const Leader = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberPage, setMemberPage] = useState(1);
  const [memberEntriesPerPage, setMemberEntriesPerPage] = useState(10);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [actualSearchTerm, setActualSearchTerm] = useState("");
  const [searchFilter, setSearchFilter] = useState("name");
  const [designationList, setDesignationList] = useState([]);
  const [designationsLoading, setDesignationsLoading] = useState(false);

  const fixedStatus = "approved";

  const [membersData, setMembersData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const userId = getCookie("userId");
  const role = getCookie("role");

  const { data: permissionsData } = useUserModulePermissions(
    "Leadership Role",
    userId,
    {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    }
  );

  let canView = false;
  let canEdit = false;
  let canDelete = false;

  if (role === "admin") {
    canView = true;
    canEdit = true;
    canDelete = true;
  } else if (role === "maintainer") {
    const modulePermissions = permissionsData?.data?.permissions?.find(
      (p) => p.module === "Leadership Role"
    );

    canView = !!modulePermissions?.view;
    canEdit = !!modulePermissions?.edit;
    canDelete = !!modulePermissions?.delete;
  }

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [
      "members",
      memberPage,
      memberEntriesPerPage,
      actualSearchTerm,
      searchFilter,
    ],
    queryFn: () =>
      getMembersByStatus(fixedStatus, memberPage, memberEntriesPerPage, {
        searchTerm: actualSearchTerm,
        searchFilter: searchFilter,
      }),
    keepPreviousData: true,
    enabled: canView,
  });

  useEffect(() => {
    const fetchAllDesignations = async () => {
      if (!canView) return;

      setDesignationsLoading(true);

      try {
        const response = await getAllDesignations();
        if (response?.data?.data) {
          setDesignationList(response.data.data);
        } else {
          setDesignationList([]);
        }
      } catch (error) {
        console.error("Error fetching designations:", error);
        setDesignationList([]);
      } finally {
        setDesignationsLoading(false);
      }
    };

    fetchAllDesignations();
  }, [canView]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const urlPage = parseInt(url.searchParams.get("page") || "1", 10);
    setMemberPage(urlPage > 0 ? urlPage : 1);
  }, []);

  useEffect(() => {
    if (data?.data?.members) {
      const transformedMembers = data.data.members.map((member) => ({
        id: member._id,
        name: member.fullName,
        email: member.email,
        avatar: getInitials(member.fullName),
        membershipId: member.membershipId,
        mobile: member.mobile,
        status: member.status,
        membershipType: member.membershipType,
        createdAt: member.createdAt,
        isEmailVerified: member.isEmailVerified,
        isMobileVerified: member.isMobileVerified,
        roles: member.leadershipRoles || [],
      }));

      setMembersData(transformedMembers);
      setTotalPages(data.data.totalPages || 1);
      setTotalDocs(data.data.totalDocs || 0);
      setHasMore(data.data.hasMore || false);
    }
  }, [data]);

  console.log(permissionsData, "permissionsData");

  const searchFilterOptions = [
    { value: "name", label: "Name" },
    { value: "memberId", label: "Member ID" },
  ];

  const entriesPerPageOptions = [
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 50, label: "50" },
  ];

  const getInitials = (fullName) => {
    if (!fullName) return "??";
    const names = fullName.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1]}`.toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const handleSearch = () => {
    if (!canView) return;
    setActualSearchTerm(memberSearchTerm);
    setMemberPage(1);

    const url = new URL(window.location.href);
    url.searchParams.set("page", "1");
    window.history.pushState({}, "", url);
  };

  const handleFilterChange = (filterValue) => {
    if (!canView) return;
    setSearchFilter(filterValue);
    setMemberSearchTerm("");

    if (actualSearchTerm) {
      setActualSearchTerm("");
      setMemberPage(1);
      const url = new URL(window.location.href);
      url.searchParams.set("page", "1");
      window.history.pushState({}, "", url);
    }
  };

  const getSearchPlaceholder = () => {
    switch (searchFilter) {
      case "name":
        return "Search by name...";
      case "memberId":
        return "Search by member ID...";
      default:
        return "Search members...";
    }
  };

  const openModal = (member) => {
    if (!canEdit) {
      console.warn("No permission to edit member roles");
      return;
    }
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  const handleMemberPageChange = (pageNum) => {
    if (!canView) return;
    setMemberPage(pageNum);

    const url = new URL(window.location.href);
    url.searchParams.set("page", String(pageNum));
    window.history.pushState({}, "", url);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEntriesChange = (selectedOption) => {
    if (!canView) return;
    setMemberEntriesPerPage(selectedOption.value);
    setMemberPage(1);

    const url = new URL(window.location.href);
    url.searchParams.set("page", "1");
    window.history.pushState({}, "", url);
  };

  const handleMemberUpdate = (updatedMember) => {
    if (!canEdit) return;
    const updatedMembers = membersData.map((member) =>
      member.id === updatedMember.id ? updatedMember : member
    );
    setMembersData(updatedMembers);
    setSelectedMember(updatedMember);
  };

  const clearSearch = () => {
    if (!canView) return;
    setMemberSearchTerm("");
    setActualSearchTerm("");
    setMemberPage(1);
    const url = new URL(window.location.href);
    url.searchParams.set("page", "1");
    window.history.pushState({}, "", url);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const memberStartIndex = (memberPage - 1) * memberEntriesPerPage + 1;
  const memberEndIndex = Math.min(memberPage * memberEntriesPerPage, totalDocs);

  if (isLoading || designationsLoading) {
    return (
      <div className="min-h-screen mt-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {designationsLoading
              ? "Loading designations..."
              : "Loading members..."}
          </p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen mt-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load members</p>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-4">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Leadership Management
              </h1>
              <p className="text-gray-600">
                Manage member roles and leadership positions
              </p>
            </div>
          </div>

          {canView && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
              <div className="grid grid-cols-1 gap-4 items-end">
                <div className="lg:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search By:
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {searchFilterOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                          searchFilter === option.value
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="searchFilter"
                          value={option.value}
                          checked={searchFilter === option.value}
                          onChange={(e) => handleFilterChange(e.target.value)}
                          className="!sr-only"
                        />
                        <div
                          className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                            searchFilter === option.value
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300"
                          }`}
                        >
                          {searchFilter === option.value && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                        <span className="text-sm font-medium">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="col-span-2 w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Term:
                  </label>
                  <div className="flex items-center gap-3 w-full">
                    <div className="relative w-full">
                      <input
                        type="text"
                        value={memberSearchTerm}
                        onChange={(e) => setMemberSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-3 h-[45px] border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4f22d8]"
                        placeholder={getSearchPlaceholder()}
                      />
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSearch}
                        disabled={!memberSearchTerm.trim()}
                        className="flex-1 bg-[#4f22d8] text-white px-4 h-[45px] rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Search
                      </button>
                      {(memberSearchTerm || actualSearchTerm) && (
                        <button
                          onClick={clearSearch}
                          className="px-3 py-2 text-sm text-gray-600 h-[45px] hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {actualSearchTerm && canView && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">{totalDocs}</span> member(s) found
                when searching by{" "}
                <strong>
                  {searchFilter === "memberId" ? "Member ID" : "Name"}
                </strong>{" "}
                for "<strong>{actualSearchTerm}</strong>"
              </p>
            </div>
          )}

          {canView && (
            <div className="flex items-center gap-4 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                <div className="w-20 mt-10">
                  <CustomSelect
                    value={entriesPerPageOptions.find(
                      (option) => option.value === memberEntriesPerPage
                    )}
                    onChange={handleEntriesChange}
                    options={entriesPerPageOptions}
                    isClearable={false}
                  />
                </div>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              {!isLoading && !isError && (
                <div className="text-sm text-gray-600">
                  Showing {memberStartIndex} to {memberEndIndex} of {totalDocs}{" "}
                  entries
                  {actualSearchTerm && <span className="ml-1">(filtered)</span>}
                </div>
              )}
            </div>
          )}
        </div>

        {canView && (
          <MembersTable
            members={membersData}
            designations={designationList}
            isLoading={isLoading || isFetching}
            isError={isError}
            onOpenModal={openModal}
            currentPage={memberPage}
            entriesPerPage={memberEntriesPerPage}
            totalPages={totalPages}
            onPageChange={handleMemberPageChange}
            permissionsData={permissionsData?.data ?? null}
            canEdit={canEdit}
          />
        )}

        {isModalOpen && selectedMember && canEdit && (
          <MemberRoleModal
            isOpen={isModalOpen}
            member={selectedMember}
            designations={designationList}
            onClose={closeModal}
            onMemberUpdate={handleMemberUpdate}
            designationList={designationList}
            refetch={refetch}
            getInitials={getInitials}
          />
        )}
      </div>
    </div>
  );
};

export default Leader;
